/* eslint-disable @typescript-eslint/no-use-before-define */
import {
    Action,
    ActionReducerMap,
    createFeatureSelector,
    createSelector,
    StoreModule,
} from '@ngrx/store';
import { includes } from 'lodash-es';
import { RideCondition } from 'src/app/models/ride-condition';
import { RideConditionTileCache } from 'src/app/models/ride-condition-cache';
import {
    RideConditionAction,
    SetActiveRideCondition,
    SetRideConditionCache,
    SetRideConditionsInView,
    UpdateRideConditionsInView,
    UpdateRideConditionTileCache,
} from './ride-condition.actions';

const featureName = 'rideCondition';

export interface RideConditionState {
    activeRideCondition?: RideCondition;
    rideConditions: RideCondition[];
    rideConditionTileCaches: RideConditionTileCache[];
}

const reducerMap: ActionReducerMap<RideConditionState> = {
    activeRideCondition: activeRideConditionReducer,
    rideConditionTileCaches: rideConditionTileCachesReducer,
    rideConditions: rideConditionInViewReducer,
};

export const selectRideConditionState =
    createFeatureSelector<RideConditionState>(featureName);
export const selectActiveRideCondition = createSelector(
    selectRideConditionState,
    (state) => state.activeRideCondition
);
export const selectRideConditionTileCaches = createSelector(
    selectRideConditionState,
    (state) => {
        return state.rideConditionTileCaches;
    }
);
export const selectRideConditionsInView = createSelector(
    selectRideConditionState,
    (state) => state.rideConditions
);

export const rideConditionStoreModule = StoreModule.forFeature(
    featureName,
    reducerMap
);

function activeRideConditionReducer(
    state: RideCondition,
    action: Action
): RideCondition | undefined {
    switch (action.type) {
        case RideConditionAction.setActiveRideCondition:
            return (action as SetActiveRideCondition).rideCondition?.clone();
        default:
            return state;
    }
}

function rideConditionTileCachesReducer(
    state: RideConditionTileCache[] = [],
    action: Action
): RideConditionTileCache[] {
    let newCaches: RideConditionTileCache[];
    let newCacheIds: string[];
    let filteredUnexpiredCaches: RideConditionTileCache[];

    switch (action.type) {
        case RideConditionAction.setRideConditionCache:
            return [...(action as SetRideConditionCache).rideConditionCaches];
        case RideConditionAction.updateRideConditionTileCache:
            newCaches = (action as UpdateRideConditionTileCache).tileCaches;
            newCacheIds = newCaches.map((cache) => cache.tileId);

            // get unexpired caches that are not being updated
            filteredUnexpiredCaches = state.reduce<RideConditionTileCache[]>(
                (result, cache) => {
                    if (
                        !cache.isExpired &&
                        !includes(newCacheIds, cache.tileId)
                    ) {
                        // not cloning because rideConditionCaches are readonly
                        result.push(cache);
                    }

                    return result;
                },
                []
            );

            return [...filteredUnexpiredCaches, ...newCaches];
        default:
            return state;
    }
}

function rideConditionInViewReducer(
    state: RideCondition[] = [],
    action: Action
): RideCondition[] {
    switch (action.type) {
        case RideConditionAction.setRideConditionsInView:
            return [...(action as SetRideConditionsInView).rideConditions];
        case RideConditionAction.updateRideConditionsInView:
            return [
                ...state.filter((condition) => !condition.isExpired),
                ...(action as UpdateRideConditionsInView).rideConditions,
            ];
        default:
            return state;
    }
}
